import { useState, useRef, useEffect } from 'react';
import { posthog } from './posthog';

// ============================================================================
// Types
// ============================================================================

interface PlayerProps {
  audioUrl: string;
  durationSeconds: number;
  queueItemId?: string;
  title?: string;
}

// ============================================================================
// Brand Icons (SVG)
// ============================================================================

const IconPlay = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);

const IconPause = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="4" width="4" height="16"/>
    <rect x="14" y="4" width="4" height="16"/>
  </svg>
);

const IconSkipBack = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 19 2 12 11 5 11 19"/>
    <polygon points="22 19 13 12 22 5 22 19"/>
  </svg>
);

const IconSkipForward = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 19 22 12 13 5 13 19"/>
    <polygon points="2 19 11 12 2 5 2 19"/>
  </svg>
);

const IconVolume = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
  </svg>
);

const IconVolumeLow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
  </svg>
);

const IconVolumeMute = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <line x1="23" y1="9" x2="17" y2="15"/>
    <line x1="17" y1="9" x2="23" y2="15"/>
  </svg>
);

// ============================================================================
// Helpers
// ============================================================================

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// ============================================================================
// Player Component
// ============================================================================

export function Player({ audioUrl, durationSeconds, queueItemId, title }: PlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Update time display
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || durationSeconds);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      posthog.capture('playback.complete', {
        durationSeconds: audio.duration,
        currentTimeSeconds: audio.currentTime,
        progressPercent: audio.duration ? Math.round((audio.currentTime / audio.duration) * 100) : undefined,
        queueItemId,
        title,
      });
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [durationSeconds]);

  // Sync playback rate
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Sync volume and mute state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      posthog.capture('playback.pause', {
        durationSeconds: audio.duration,
        currentTimeSeconds: audio.currentTime,
        progressPercent: audio.duration ? Math.round((audio.currentTime / audio.duration) * 100) : undefined,
        queueItemId,
        title,
      });
    } else {
      audio.play();
      posthog.capture('playback.start', {
        durationSeconds: audio.duration,
        currentTimeSeconds: audio.currentTime,
        progressPercent: audio.duration ? Math.round((audio.currentTime / audio.duration) * 100) : undefined,
        queueItemId,
        title,
      });
    }
    setIsPlaying(!isPlaying);
  }

  function handleSeek(e: React.ChangeEvent<HTMLInputElement>) {
    const audio = audioRef.current;
    if (!audio) return;

    const time = Number(e.target.value);
    audio.currentTime = time;
    setCurrentTime(time);
  }

  function skip(seconds: number) {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = Math.max(0, Math.min(duration, audio.currentTime + seconds));
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }

  function cyclePlaybackRate() {
    const rates = [1, 1.25, 1.5, 1.75, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    setPlaybackRate(rates[nextIndex]);
  }

  function handleVolumeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newVolume = Number(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  }

  function toggleMute() {
    setIsMuted(!isMuted);
  }

  function getVolumeIcon() {
    if (isMuted || volume === 0) return <IconVolumeMute />;
    if (volume < 0.5) return <IconVolumeLow />;
    return <IconVolume />;
  }

  return (
    <div className="audio-controls">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      <div className="play-button-container">
        <button
          className="skip-button"
          onClick={() => skip(-15)}
          aria-label="Skip back 15 seconds"
        >
          <IconSkipBack />
        </button>

        <button
          className="play-button"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <IconPause /> : <IconPlay />}
        </button>

        <button
          className="skip-button"
          onClick={() => skip(15)}
          aria-label="Skip forward 15 seconds"
        >
          <IconSkipForward />
        </button>
      </div>

      <div className="seek-container">
        <span className="time-display">{formatTime(currentTime)}</span>
        <input
          type="range"
          className="seek-bar"
          min={0}
          max={duration || 100}
          value={currentTime}
          onChange={handleSeek}
          aria-label="Seek"
        />
        <span className="time-display right">{formatTime(duration)}</span>
      </div>

      <div className="secondary-controls">
        <button
          className={`control-button ${playbackRate !== 1 ? 'active' : ''}`}
          onClick={cyclePlaybackRate}
        >
          {playbackRate}x
        </button>

        <div className="volume-control">
          <button
            className="volume-button"
            onClick={toggleMute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {getVolumeIcon()}
          </button>
          <input
            type="range"
            className="volume-slider"
            min={0}
            max={1}
            step={0.05}
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            aria-label="Volume"
          />
        </div>
      </div>
    </div>
  );
}
